// On-ship boon visualization: attachments that appear on/around the player
// craft as boons are acquired (extra cannon, shield bubble, hull plate,
// projectile orbs, and a HUD boon rack). Deterministic from sim state.

import * as THREE from 'three';
import type { Player } from '../types.js';
import { getUpgradeMods } from '../sim/upgrade-system.js';

export class BoonVisuals {
  readonly group: THREE.Group;
  private readonly laserBarrel: THREE.Mesh;
  private readonly shieldBubble: THREE.Mesh;
  private readonly hullPlate: THREE.Mesh;
  private readonly projectileOrbs: THREE.Mesh[] = [];
  private readonly orbitingGroup: THREE.Group;
  private readonly railCoils: THREE.Group;
  private readonly fireFins: THREE.Mesh[];
  private readonly damageStuds: THREE.Mesh[];
  private readonly thrustPods: THREE.Mesh[];
  private readonly magnetDish: THREE.Mesh;
  private readonly critEye: THREE.Mesh;
  private readonly dashStripes: THREE.Mesh[];
  private readonly hpPlate: THREE.Mesh;
  private readonly splitFork: THREE.Mesh[];
  private readonly scatterBarrel: THREE.Mesh;
  private readonly seekerPod: THREE.Group;
  private readonly lanceSpike: THREE.Mesh;

  constructor() {
    this.group = new THREE.Group();

    // Laser cannon barrel: a small gold emitter on the ship's right side.
    const barrelGeo = new THREE.PlaneGeometry(0.12, 0.5);
    barrelGeo.translate(0.35, 0.1, 0);
    const barrelMat = new THREE.MeshBasicMaterial({
      color: 0xffd36b,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.laserBarrel = new THREE.Mesh(barrelGeo, barrelMat);
    this.laserBarrel.position.set(0.25, -0.15, 0.06);
    this.laserBarrel.rotation.z = -0.2;
    this.group.add(this.laserBarrel);

    // Shield bubble: translucent blue bubble around the ship.
    const shieldGeo = new THREE.RingGeometry(0.9, 1.05, 32);
    const shieldMat = new THREE.MeshBasicMaterial({
      color: 0x4db8ff,
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.shieldBubble = new THREE.Mesh(shieldGeo, shieldMat);
    this.shieldBubble.position.z = -0.01;
    this.group.add(this.shieldBubble);

    // Hull plate: a small blue armored plate on the ship's left side.
    const plateGeo = new THREE.PlaneGeometry(0.25, 0.15);
    const plateMat = new THREE.MeshBasicMaterial({
      color: 0x4db8ff,
      transparent: true,
      opacity: 0.8,
      toneMapped: false,
    });
    this.hullPlate = new THREE.Mesh(plateGeo, plateMat);
    this.hullPlate.position.set(-0.35, -0.2, 0.06);
    this.hullPlate.rotation.z = 0.3;
    this.group.add(this.hullPlate);

    // Projectile orbs: small gold orbs orbiting the ship (one per extra projectile).
    this.orbitingGroup = new THREE.Group();
    this.group.add(this.orbitingGroup);
    for (let i = 0; i < 4; i++) {
      const orbGeo = new THREE.CircleGeometry(0.08, 8);
      const orbMat = new THREE.MeshBasicMaterial({
        color: 0xffd36b,
        transparent: true,
        opacity: 0.9,
        toneMapped: false,
      });
      const orb = new THREE.Mesh(orbGeo, orbMat);
      orb.position.set(0.9, 0, 0.05);
      this.projectileOrbs.push(orb);
      this.orbitingGroup.add(orb);
    }

    // Railgun coils: two small ice-blue rings clamped beside the lance
    // barrel, shown while any Railgun Coils rank is held.
    const coilGeo = new THREE.RingGeometry(0.09, 0.15, 12);
    const coilMat = new THREE.MeshBasicMaterial({
      color: 0x9fefff,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.railCoils = new THREE.Group();
    for (let i = 0; i < 2; i++) {
      const ring = new THREE.Mesh(coilGeo, coilMat);
      ring.position.set(0, i * 0.16, 0);
      this.railCoils.add(ring);
    }
    this.railCoils.position.set(-0.25, 0.3, 0.06);
    this.railCoils.rotation.z = 0.5;
    this.group.add(this.railCoils);

    // Scattergun barrel stub: a short, stubby gold second barrel on the
    // ship's left side, shown while the Shard Scattergun weapon is held.
    const stubGeo = new THREE.PlaneGeometry(0.16, 0.28);
    stubGeo.translate(-0.35, 0.08, 0);
    const stubMat = new THREE.MeshBasicMaterial({
      color: 0xffd36b,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.scatterBarrel = new THREE.Mesh(stubGeo, stubMat);
    this.scatterBarrel.position.set(-0.25, -0.15, 0.06);
    this.scatterBarrel.rotation.z = 0.2;
    this.group.add(this.scatterBarrel);

    // Seeker missile pod: a compact 2×2 cluster of ice-blue tubes on the
    // ship's right side, shown while the Seeker Swarm weapon is held.
    const podDotGeo = new THREE.CircleGeometry(0.05, 8);
    const podMat = new THREE.MeshBasicMaterial({
      color: 0x9fefff,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
    });
    this.seekerPod = new THREE.Group();
    for (let i = 0; i < 4; i++) {
      const dot = new THREE.Mesh(podDotGeo, podMat);
      dot.position.set((i % 2 === 0 ? -1 : 1) * 0.07, (i < 2 ? 1 : -1) * 0.07, 0);
      this.seekerPod.add(dot);
    }
    this.seekerPod.position.set(0.38, 0.32, 0.06);
    this.group.add(this.seekerPod);

    // Lance nose spike: a slim gold spike protruding past the hull nose,
    // shown while the Spear-Jelly Lance weapon is held. Base sits inside
    // the hull (nose tips range ~0.41–0.60) so only the tip sticks out.
    const spikeGeo = new THREE.PlaneGeometry(0.06, 0.4);
    spikeGeo.translate(0, 0.2, 0); // anchor the base at the mount point
    const spikeMat = new THREE.MeshBasicMaterial({
      color: 0xffd36b,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.lanceSpike = new THREE.Mesh(spikeGeo, spikeMat);
    this.lanceSpike.position.set(0, 0.32, 0.06);
    this.group.add(this.lanceSpike);

    // --- Per-boon attachments: every boon predictably marks the hull. ---
    const part = (
      geo: THREE.BufferGeometry,
      color: number,
      x: number,
      y: number,
      rot = 0
    ): THREE.Mesh => {
      const m = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.9,
          toneMapped: false,
          side: THREE.DoubleSide,
        })
      );
      m.position.set(x, y, 0.06);
      m.rotation.z = rot;
      m.visible = false;
      this.group.add(m);
      return m;
    };

    // Fire rate: twin antenna fins flanking the tail.
    this.fireFins = [
      part(new THREE.PlaneGeometry(0.05, 0.3), 0x9fefff, -0.22, -0.45, 0.15),
      part(new THREE.PlaneGeometry(0.05, 0.3), 0x9fefff, 0.22, -0.45, -0.15),
    ];
    // Damage: red studs up the nose.
    this.damageStuds = [
      part(new THREE.CircleGeometry(0.05, 8), 0xff4d6b, -0.1, 0.38),
      part(new THREE.CircleGeometry(0.05, 8), 0xff4d6b, -0.04, 0.48),
    ];
    // Move speed: blue thruster pods at the wingtips.
    this.thrustPods = [
      part(new THREE.PlaneGeometry(0.08, 0.16), 0x4db8ff, -0.5, 0.05, 0.3),
      part(new THREE.PlaneGeometry(0.08, 0.16), 0x4db8ff, 0.5, 0.05, -0.3),
    ];
    // Magnet: a small mint dish at the left wing root.
    this.magnetDish = part(new THREE.RingGeometry(0.07, 0.12, 10), 0x3dffc4, -0.35, 0.1);
    // Crit: a gold eye at the hull center.
    this.critEye = part(new THREE.RingGeometry(0.05, 0.09, 10), 0xffd36b, 0, 0.05);
    // Dash: slim gold stripes on both wings.
    this.dashStripes = [
      part(new THREE.PlaneGeometry(0.03, 0.34), 0xffd36b, -0.48, -0.15, -0.2),
      part(new THREE.PlaneGeometry(0.03, 0.34), 0xffd36b, 0.48, -0.15, 0.2),
    ];
    // Max HP: a red armor plate on the right mid-hull.
    this.hpPlate = part(new THREE.PlaneGeometry(0.2, 0.14), 0xff4d6b, 0.28, 0.1, -0.2);
    // Split Rounds: a tiny gold fork at the right tail.
    this.splitFork = [
      part(new THREE.PlaneGeometry(0.04, 0.2), 0xffd36b, 0.3, -0.33, 0.5),
      part(new THREE.PlaneGeometry(0.04, 0.2), 0xffd36b, 0.38, -0.33, -0.5),
    ];
  }

  update(player: Player, time: number): void {
    // Shield bubble visible when the player has any max shield.
    this.shieldBubble.visible = player.max_shield > 0;
    if (this.shieldBubble.visible) {
      const pulse = 1 + Math.sin(time * 4) * 0.05;
      this.shieldBubble.scale.set(pulse, pulse, 1);
      const mat = this.shieldBubble.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.15 + (player.shield / player.max_shield) * 0.15;
    }

    // Hull plate visible when the player has any max hull.
    this.hullPlate.visible = player.max_hull > 0;

    // Projectile orbs: one per extra projectile from upgrades.
    const bonus = Math.max(0, player.weapons[0] ? Math.floor(player.weapons[0].level - 1) : 0);
    const count = Math.min(this.projectileOrbs.length, bonus);
    for (let i = 0; i < this.projectileOrbs.length; i++) {
      this.projectileOrbs[i].visible = i < count;
    }
    this.orbitingGroup.rotation.z = time * 1.5;

    // Railgun coils visible while the pierce boon is held.
    const mods = getUpgradeMods(player);
    this.railCoils.visible = mods.projectilePierceBonus > 0;

    // Weapon attachments: each visible exactly while its weapon is held.
    this.scatterBarrel.visible = player.weapons.some(w => w.def_id === 'weapon.shard-scattergun');
    this.seekerPod.visible = player.weapons.some(w => w.def_id === 'weapon.seeker-swarm');
    this.lanceSpike.visible = player.weapons.some(w => w.def_id === 'weapon.spear-jelly-lance');

    // Per-boon attachments: every boon kind leaves a predictable mark.
    for (const m of this.fireFins) m.visible = mods.fireRateMult > 0;
    for (const m of this.damageStuds) m.visible = mods.damageMult > 0;
    for (const m of this.thrustPods) m.visible = mods.moveSpeedMult > 0;
    this.magnetDish.visible = mods.magnetRadiusMult > 0;
    this.critEye.visible = mods.critChanceBonus > 0;
    const dashHaste = (player as unknown as { hasteFromUpgrades?: number }).hasteFromUpgrades ?? 0;
    for (const m of this.dashStripes) m.visible = dashHaste > 0;
    this.hpPlate.visible = player.max_hp > 100;
    for (const m of this.splitFork) m.visible = mods.bulletSplitBonus > 0;
  }
}
